import { useEffect } from "react";
import React, { useState } from "react";
import {
  CardFooter,
  Table,
  Row,
  Button,
  Input,
  Col,
  Spinner,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { getLabels, deleteLabel } from "store/actions/labelActions";
import Pagination from "components/shared/Pagination";
import DebouncedSearch from "components/shared/DebouncedSearch";
import LabelModal from "./LabelModal";
import ConfirmationModal from "components/Modals/ConfirmationModal";
import { toast } from "react-toastify";
import usePermissions from "hooks/usePermissions";

const MenuLabels = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { labels, labelsRest } = useSelector((state) => state.label);

  const {
    add: addAccess,
    edit: editAccess,
    delete: deleteAccess,
    get: getAccess,
  } = usePermissions();
  const [isFetching, setIsFetching] = useState(false);
  const [hitsPerPage, setHitsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [labelModal, setLabelModal] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [activeLabel, setActiveLabel] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSearch = (debouncedValue) => {
    setDebouncedSearch(debouncedValue);
  };

  const labelModalToggler = () => {
    if (labelModal) {
      setActiveLabel(null);
      setEditMode(false);
    }
    setLabelModal(!labelModal);
  };

  const confirmationModalToggler = () => {
    if (confirmationModal) {
      setActiveLabel(null);
    }
    setConfirmationModal(!confirmationModal);
  };

  const initiateEditLabel = (label) => {
    setActiveLabel(label);
    setEditMode(true);
    labelModalToggler();
  };

  const initiateDeleteLabel = (label) => {
    setActiveLabel(label);
    confirmationModalToggler();
  };

  const handleDeleteLabel = () => {
    setIsDeleting(true);
    dispatch(
      deleteLabel(
        activeLabel?.id,
        activeLabel?.labelURL || "",
        () => {
          setTimeout(() => {
            confirmationModalToggler();
            setIsDeleting(false);
            dispatch(
              getLabels(
                user?.restaurantID,
                debouncedSearch,
                hitsPerPage,
                currentPage
              )
            );
            toast.success("Label Deleted Successfully");
          }, 3000);
        },
        () => {
          setIsDeleting(false);
        }
      )
    );
  };

  useEffect(() => {
    setIsFetching(true);
    dispatch(
      getLabels(user?.restaurantID, debouncedSearch, hitsPerPage, currentPage)
    ).then(() => setIsFetching(false));
  }, [debouncedSearch, hitsPerPage, currentPage]);

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch]);

  return (
    <>
      <Row className="justify-content-end px-4 py-3">
        <Col sm="5" lg="4">
          <DebouncedSearch
            placeholder="Search label by name"
            onSearch={handleSearch}
          />
        </Col>
        <Col sm="4" lg="3" className="mt-3 mt-sm-0">
          <Input
            type="select"
            id="exampleCustomSelect"
            name="customSelect"
            value={hitsPerPage}
            onChange={(e) => {
              setHitsPerPage(e.target.value);
              setCurrentPage(0);
            }}
          >
            <option value="5">Show Entries</option>
            <option value="10">10</option>
            <option value="15">15</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </Input>
        </Col>
        {addAccess && (
          <Col sm="3" lg="2" className="mt-3 mt-sm-0">
            <Button color="primary" onClick={labelModalToggler} block>
              Add
            </Button>
          </Col>
        )}
      </Row>
      <Table className="align-items-center table-flush" responsive>
        <thead className="thead-light">
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Description</th>
            <th scope="col">Image</th>
            {editAccess && <th scope="col">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {isFetching ? (
            <tr>
              <td colSpan="4" className="text-center py-5">
                <Spinner color="primary" />
              </td>
            </tr>
          ) : getAccess && labels?.length > 0 ? (
            labels?.map((label) => (
              <tr key={label.id}>
                <th>{label?.labelName}</th>
                <td style={{ maxWidth: "100px" }}>
                  {label?.labelDescription ? (
                    <div className="text-truncate">
                      {label?.labelDescription}
                    </div>
                  ) : (
                    "-"
                  )}
                </td>
                <td>
                  {label?.labelURL ? (
                    <img src={label?.labelURL} alt="Label" width={50} />
                  ) : (
                    "N/A"
                  )}
                </td>
                {(editAccess || deleteAccess) && (
                  <td>
                    {editAccess && (
                      <Button
                        className="btn-sm"
                        color="primary"
                        disabled={label?.restaurantID == "null" || !editAccess}
                        onClick={() => initiateEditLabel(label)}
                      >
                        Edit
                      </Button>
                    )}
                    {deleteAccess && (
                      <Button
                        className="btn-sm"
                        color="danger"
                        disabled={
                          label?.restaurantID == "null" || !deleteAccess
                        }
                        onClick={() => initiateDeleteLabel(label)}
                      >
                        Delete
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="4" className="text-center py-5">
                No Label
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <CardFooter className="py-4">
        <Pagination
          currentPage={currentPage}
          totalPages={labelsRest?.nbPages}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </CardFooter>
      <LabelModal
        isOpen={labelModal}
        toggle={labelModalToggler}
        editMode={editMode}
        activeLabel={activeLabel}
      />
      <ConfirmationModal
        title="Delete Label"
        text={
          <>
            <h3>Are you sure you want to delete this label?</h3>
            <span style={{ fontSize: "14px" }}>
              <strong>Note: </strong>
              Deleting label may cause some issues if they are added in any of
              the items.
            </span>
          </>
        }
        isOpen={confirmationModal}
        toggle={confirmationModalToggler}
        isLoading={isDeleting}
        onConfirmation={handleDeleteLabel}
      />
    </>
  );
};

export default MenuLabels;
